import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Lock, Shield, Zap, Briefcase, TrendingUp, Target, Clock, Wrench, BarChart3, Plus } from "lucide-react";
import StickyCTABar from "@/components/StickyCTABar";
import CountdownTimer from "@/components/CountdownTimer";
import ExitIntentModal from "@/components/ExitIntentModal";
import { captureUTMParams } from "@/utils/utmUtils";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EVEngineersPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [offerExpired, setOfferExpired] = useState(false);

  // Capture UTM params on mount (Task 3)
  useEffect(() => { captureUTMParams(); }, []);

  const startAnalysis = () => navigate("/intake");

  // FAQ state
  const [faqOpen, setFaqOpen] = useState(0);

  const stats = [
    { value: "4,800+", label: "EV Resumes Analyzed" },
    { value: "2.8x", label: "More Callbacks" },
    { value: "89%", label: "ATS Pass Rate After Fix" },
    { value: "₹12.6L", label: "Avg. Placed Salary" },
  ];

  const companies = [
    { name: "Tata Motors", logo: "/logos/ev_tata.png" },
    { name: "Ola Electric", logo: "/logos/ev_ola.png" },
    { name: "Ather", logo: "/logos/ev_ather.png" },
    { name: "Mahindra EV", logo: "/logos/ev_mahindra.png" },
    { name: "Bosch", logo: "/logos/ev_bosch.png" },
    { name: "Continental", logo: "/logos/ev_continental.png" },
    { name: "Hyundai", logo: "/logos/ev_hyundai.png" },
    { name: "MG Motor", logo: "/logos/ev_mg.png" },
  ];

  const features = [
    {
      icon: <Clock className="w-5 h-5" />,
      tag: "EV Engineer Critical",
      title: "ATS Readiness Analyzer",
      desc: "That SolidWorks screenshot on your resume? ATS reads it as blank space. We check format, keyword alignment, section structure, and parser readability — then score you out of 100 against live EV job descriptions.",
      example: "Scans for \"MATLAB\", \"AUTOSAR\", \"BMS\", \"Simulink\", \"HIL testing\" + 200 EV keywords"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "ATS Blocker Fix Engine",
      desc: "Don't just see problems — fix them. Get exact, copy-paste-ready rewrites for every blocker. We turn vague engineering descriptions into ATS-optimized, impact-driven bullet points that recruiters notice.",
      example: "Before: \"Worked on EV powertrain design\" → After: \"Designed 48V powertrain for e-scooter using MATLAB/Simulink, improving motor efficiency by 12%\""
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      tag: "EV Engineer Critical",
      title: "Career Velocity Simulator",
      desc: "Stay at a legacy OEM or jump to an EV startup? Specialize in battery systems or embedded ADAS? See how each decision changes your salary trajectory over 12, 24, and 36 months — with real Indian EV market data.",
      example: "Simulates: Legacy auto (₹4.5L) → Ather Sr. Battery Engineer (₹16L) in 14 months"
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      title: "Smart Job Matching",
      desc: "Stop scrolling Naukri hoping an EV role appears. We score every opening against your exact domain — powertrain, BMS, ADAS, charging infra — and show only roles where your profile gets responses.",
      example: "\"92% match: Battery Systems Engineer at Ola Electric — your thermal management + Simulink exp is a direct fit\""
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      tag: "EV Engineer Critical",
      title: "EV Market Signals & Trends",
      desc: "Know which EV specializations are exploding (battery engineers command 20–25% premium) and which are plateauing — before you invest months upskilling in the wrong direction.",
      example: "\"Battery + thermal management roles up 52% YoY in Pune\" — SiC/GaN skills carry highest premium"
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: "Legacy-to-EV Transition Plan",
      desc: "Stuck at a traditional auto company doing ICE work? We map the exact skill gaps between your current role and EV roles at Tata, Ola, or Ather — and build a 30/60/90-day roadmap to get you there.",
      example: "Gap analysis: \"Add AUTOSAR + battery thermal modeling → eligible for 14 more EV openings\""
    },
  ];

  const testimonials = [
    {
      initials: "VP",
      name: "Vikram Patel",
      img: "/avatars/vikram_patel.jpg",
      role: "Legacy auto (₹4L) → Tata Motors EV Powertrain Engineer, Pune",
      rating: 5,
      text: "I had BAJA SAE experience, SolidWorks, and a mech engineering degree. Applied to 150+ jobs — zero calls. PlacementOS found 9 ATS blockers including project images that were completely invisible to parsers. Rewrote everything in one night. 4 callbacks that week.",
      result: "Placed at Tata EV — ₹14 LPA"
    },
    {
      initials: "NK",
      name: "Neha Kulkarni",
      img: "/avatars/neha_kulkarni.jpg",
      role: "Auto ancillary → Ather Energy Battery Engineer, Bangalore",
      rating: 5,
      text: "The career velocity simulator was the wake-up call I needed. I was about to accept a ₹5.5L service role at an auto ancillary. PlacementOS showed me the battery engineer path — the skills gap was smaller than I thought. Bridged it in 3 months.",
      result: "Moved from ₹5.5L → ₹15L in 5 months"
    },
    {
      initials: "AR",
      name: "Aditya Rao",
      img: "/avatars/aditya_rao.jpg",
      role: "Fresher → Jr. Embedded Systems Engineer, Bosch India, Bangalore",
      rating: 5,
      text: "Fresher with an EE degree and some Arduino projects. No one was calling back. PlacementOS restructured my resume to lead with embedded systems impact instead of listing coursework. Two OEMs and one startup called within 10 days.",
      result: "3 offers — accepted ₹8 LPA at Bosch EV division"
    },
  ];

  const faqs = [
    {
      question: "What exactly do I get for ₹99?",
      answer: "A complete EV career analysis: your ATS readiness score out of 100, every blocker in your resume with exact fix recommendations, a legacy-to-EV career roadmap, smart job matches with fit scores across OEMs and startups, and a career velocity simulation. The free analysis shows your score and top blockers — you pay only for the full report."
    },
    {
      question: "Is this built specifically for EV engineers?",
      answer: "Yes. Our keyword database covers 200+ EV-specific terms — MATLAB, Simulink, AUTOSAR, BMS, Li-ion, thermal management, HIL testing, power electronics, SiC, GaN, CATIA, SolidWorks, embedded C, CAN protocols, and more. Career roadmaps differentiate between OEM, startup, and tier-1 supplier paths."
    },
    {
      question: "I'm a mechanical/electrical engineer with no EV experience. Will this help?",
      answer: "Especially. Most EV roles hire from mechanical, electrical, and electronics backgrounds — your foundation is already there. The blocker is usually how your resume presents existing skills. We reframe your experience using EV-relevant language and show the exact gap between where you are and entry-level EV roles."
    },
    {
      question: "I'm at a legacy auto company. Can this help me switch to EV?",
      answer: "This is the most common transition we support. EV companies actively recruit from legacy auto — your manufacturing, testing, and design experience is valuable. The career velocity simulator shows the exact skill gap, and typical salary jump is 2–3x."
    },
    {
      question: "Is my data safe?",
      answer: "Your resume and analysis data are encrypted end-to-end, never shared with third parties, and you can request deletion at any time. Payments are processed through secure, PCI DSS Level 1 compliant infrastructure."
    },
  ];

  // (guest upload flow removed — analysis entry now requires sign-in via startAnalysis)

  return (
    <div className="ap-root min-h-screen bg-white" data-testid="ev-engineers-page">
      {/* Exit Intent Modal */}
      <ExitIntentModal lpSlug="ev-engineers" />

      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="ap-chip ap-rise ap-rise-1" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--ap-violet)" }}></span>
                Built for EV Engineers in India
              </span>
              <h1 className="ap-display ap-rise ap-rise-2 mt-5 font-extrabold tracking-tight text-[clamp(30px,5vw,52px)] leading-[1.06] text-[var(--ap-ink)] mb-6">
                You build powertrains.<br />
                Your resume gets <span className="ap-grad-text">rejected by ATS.</span>
              </h1>
              <p className="ap-rise ap-rise-3 text-xl text-[var(--ap-ink-2)] mb-8 leading-relaxed">
                You can design a BMS from scratch. But your resume can't get past keyword filters at Tata Motors or Ather. PlacementOS finds the exact blockers killing your applications — so EV recruiters actually see your profile.
              </p>
              <div className="ap-rise ap-rise-4 mt-6">
                <button onClick={startAnalysis} className="ap-btn ap-btn-fire ap-btn-lg" data-testid="ev-engineers-hero-cta">
                  Start My Free Analysis
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative ap-rise ap-rise-2">
              {/* floating 3D chips */}
              <div className="hidden md:flex ap-glass ap-float absolute -left-6 -top-5 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <TrendingUp className="w-5 h-5 text-[var(--ap-green)]" />
                <div><div className="ap-display font-extrabold text-[var(--ap-ink)] leading-none">2.8x</div><div className="text-[11px] text-[var(--ap-muted)]">more callbacks</div></div>
              </div>
              <div className="hidden md:flex ap-glass ap-float-2 absolute -right-6 -bottom-6 z-30 items-center gap-2 px-3.5 py-2.5 rounded-2xl">
                <span className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: "var(--ap-fire)" }}>89</span>
                <div><div className="text-[12px] font-bold text-[var(--ap-ink)] leading-none">89% pass rate</div><div className="text-[11px] text-[var(--ap-muted)]">ATS after fix</div></div>
              </div>

              <div className="ap-card ap-tilt overflow-hidden p-0 relative z-10 rounded-[26px]">
                <div className="px-4 py-3 flex items-center gap-2 border-b border-[var(--ap-line)] bg-[var(--ap-canvas)]">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <span className="text-xs text-[var(--ap-muted)] ml-2">placementos.ai/ev-engineer-analysis</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-red-500">28</span>
                    </div>
                    <div>
                      <h4 className="ap-display font-semibold text-[var(--ap-ink)]">ATS Readiness Score</h4>
                      <p className="text-sm text-red-500">High rejection risk — 5 critical blockers found</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">Missing "MATLAB", "AUTOSAR", "BMS" in skills section</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-red-500 mt-0.5">✕</span>
                      <span className="text-[var(--ap-ink-2)]">CAD project images — ATS parses 0 keywords</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-[var(--ap-ink-2)]">"Worked on EV powertrain" — no specs, no metrics</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-[var(--ap-green)] mt-0.5">✓</span>
                      <span className="text-[var(--ap-ink-2)]">Fix available — estimated new score: 87</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[var(--ap-canvas)] py-12">
        <div className="ap-section max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="ap-display text-3xl md:text-4xl font-bold text-[var(--ap-ink)] mb-2">{stat.value}</div>
                <div className="text-sm text-[var(--ap-muted)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="bg-[var(--ap-canvas-2)] py-12">
        <div className="ap-section max-w-6xl text-center">
          <p className="text-[var(--ap-muted)] mb-8 font-medium">Our users have landed roles at</p>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6">
            {companies.map((c, idx) => (
              <div key={idx} className="h-10 md:h-12 flex items-center justify-center" title={c.name}>
                <img src={c.logo} alt={c.name} loading="lazy" className="h-full w-auto object-contain max-w-[140px]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The Real Problem</p>
          <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-6">
            You're not under-qualified.<br />
            You're <span className="ap-grad-text">under-optimized.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed mb-12">
            India's EV industry is growing at 49% CAGR. Tata, Ola, Ather, and Mahindra are hiring thousands. But your resume — packed with SolidWorks screenshots, vague project descriptions, and zero ATS keywords — gets auto-rejected before any hiring manager sees it.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">90%</div>
              <p className="text-[var(--ap-ink-2)]">of top EV companies use ATS — your resume is filtered before any human reads it</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">45%</div>
              <p className="text-[var(--ap-ink-2)]">higher salaries in EV vs. traditional auto — but only if you get the interview first</p>
            </div>
            <div className="ap-card p-6">
              <div className="ap-display text-4xl font-bold text-[var(--ap-violet)] mb-2">5M</div>
              <p className="text-[var(--ap-ink-2)]">new EV jobs expected by 2030 — yet most engineers can't get past the first filter</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">The PlacementOS Way</p>
          <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-6">
            Fix your resume. Then apply<br />
            where you'll <span className="ap-grad-text">actually get hired.</span>
          </h2>
          <p className="text-lg text-[var(--ap-ink-2)] leading-relaxed">
            PlacementOS scans your resume the way Workday, Greenhouse, and Naukri ATS do — finds every blocker specific to EV engineering roles — and matches you to companies where your exact skill set is in demand.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>1</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Upload your resume</h3>
              <p className="text-[var(--ap-muted)]">Drop your PDF or DOCX. Our engine parses it using the same ATS logic that Tata Motors, Ola Electric, and Bosch use.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>2</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Get your EV-specific diagnosis</h3>
              <p className="text-[var(--ap-muted)]">See your score, every blocker, and prioritized fixes — tuned for BMS, powertrain, ADAS, embedded systems, and more.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4" style={{ background: "var(--ap-fire)" }}>3</div>
              <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">Fix, match & apply smart</h3>
              <p className="text-[var(--ap-muted)]">Apply the copy-paste fixes, unlock jobs matched to your exact domain, and get a career roadmap to EV companies.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 bg-[var(--ap-canvas)]" id="features">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">What You Get</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              Every tool an EV engineer<br />needs to <span className="ap-grad-text">get placed.</span>
            </h2>
            <p className="text-lg text-[var(--ap-ink-2)]">Not a generic resume checker. Every feature understands how EV engineering hiring works in India.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                    {feature.icon}
                  </div>
                  {feature.tag && (
                    <span className="ap-chip" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>{feature.tag}</span>
                  )}
                </div>
                <h3 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-3">{feature.title}</h3>
                <p className="text-[var(--ap-muted)] mb-4">{feature.desc}</p>
                <div className="rounded-lg border-l-4 p-3 text-sm text-[var(--ap-ink-2)] italic" style={{ background: "var(--ap-violet-soft)", borderColor: "var(--ap-violet)" }}>
                  {feature.example}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10">
        <div className="ap-section max-w-6xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Real Results</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              EV engineers who stopped<br />mass-applying and <span className="ap-grad-text">got placed.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="ap-card ap-pop p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <span key={i} className="text-[var(--ap-orange)]">★</span>
                  ))}
                </div>
                <p className="text-[var(--ap-ink-2)] mb-4 leading-relaxed">{t.text}</p>
                <div className="flex items-center gap-2 text-[var(--ap-green)] font-semibold mb-4">
                  <CheckCircle className="w-5 h-5" />
                  {t.result}
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative w-12 h-12 text-white rounded-full flex items-center justify-center font-bold overflow-hidden shrink-0" style={{ background: "var(--ap-fire)" }}>
                    {t.initials}
                    {t.img && <img src={t.img} alt={t.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => e.currentTarget.remove()} />}
                  </div>
                  <div>
                    <div className="ap-display font-semibold text-[var(--ap-ink)]">{t.name}</div>
                    <div className="text-xs text-[var(--ap-muted)]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-[var(--ap-canvas)] py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-10">
            <p className="ap-eyebrow mb-4">Launch Pricing</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-4">
              One analysis. Everything you need<br />to <span className="ap-grad-text">get placed in EV.</span>
            </h2>
            <div className="mt-4">
              <CountdownTimer onExpired={() => setOfferExpired(true)} />
            </div>
          </div>
          <div className="ap-card p-8 md:p-12 relative overflow-hidden" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
            <div className="text-center mb-8">
              <span className="ap-chip mb-4" style={{ color: "#fff", border: "none", background: "var(--ap-fire)" }}>
                {offerExpired ? "Price Updated" : "Launch Offer — 90% Off"}
              </span>
              <h3 className="ap-display text-3xl font-bold text-[var(--ap-ink)] mb-2">Complete EV Career Analysis</h3>
              <p className="text-[var(--ap-muted)]">ATS diagnosis + EV career roadmap + smart job matching</p>
            </div>
            <div className="space-y-3 mb-8">
              {[
                { item: "ATS Score & Full Blocker Analysis", price: "₹499" },
                { item: "Copy-Paste Fix Recommendations", price: "₹399" },
                { item: "Legacy-to-EV Career Roadmap", price: "₹599" },
                { item: "Smart EV Job Matching + Fit Scores", price: "₹299" },
                { item: "Career Velocity Simulation", price: "₹99" },
              ].map((row, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-[var(--ap-line)]">
                  <span className="flex items-center gap-2 text-[var(--ap-ink-2)]">
                    <CheckCircle className="w-5 h-5 text-[var(--ap-green)]" />
                    {row.item}
                  </span>
                  <span className="text-[var(--ap-muted)] line-through">{row.price}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center py-4 border-t-2 border-[var(--ap-line)] mb-8">
              <span className="text-lg font-semibold text-[var(--ap-ink)]">Total Value</span>
              <span className="text-lg font-semibold text-[var(--ap-ink)]">₹1,995</span>
            </div>
            <div className="text-center mb-8">
              <p className="text-[var(--ap-muted)] mb-2">You pay today</p>
              <div className="ap-display text-6xl font-bold text-[var(--ap-ink)]">
                {offerExpired ? (
                  <><span className="text-3xl">₹</span>499<p className="text-base text-[var(--ap-muted)] mt-2 font-normal">Offer expired</p></>
                ) : (
                  <><span className="text-3xl">₹</span>99<p className="text-[var(--ap-muted)] mt-2 text-base font-normal">One-time payment · Lifetime access</p></>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={startAnalysis}
                className="ap-btn ap-btn-fire ap-btn-lg w-full"
                data-testid="ev-pricing-cta"
              >
                Start My Free Analysis
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-center text-[var(--ap-muted)] text-sm mt-4 flex items-center justify-center gap-2">
              <CheckCircle className="w-4 h-4 text-[var(--ap-green)]" />
              Free analysis first — pay only if you want the full report
            </p>
            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)]">
              <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> Secure</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
              <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> Instant</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10">
        <div className="ap-section max-w-4xl">
          <div className="text-center mb-12">
            <p className="ap-eyebrow mb-4">Questions</p>
            <h2 className="ap-display ap-h2 text-[var(--ap-ink)]">Frequently asked.</h2>
          </div>
          <div className="mt-12 space-y-5">
            {faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="ap-card overflow-hidden transition-shadow"
                  style={open ? { borderColor: "var(--ap-violet)", boxShadow: "0 18px 50px -24px rgba(91,61,245,.4)" } : {}}>
                  <button onClick={() => setFaqOpen(open ? -1 : i)} className="group w-full flex items-center justify-between gap-4 text-left px-6 py-6 bg-transparent border-0 cursor-pointer">
                    <span className="ap-display font-bold text-[16.5px] text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">{f.question}</span>
                    <span className="shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors"
                      style={{ background: open ? "var(--ap-violet)" : "var(--ap-violet-soft)", color: open ? "#fff" : "var(--ap-violet)" }}>
                      <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                    </span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-[15px] text-[var(--ap-muted)] leading-relaxed">{f.answer}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="ap-mesh ap-grid-overlay relative py-10">
        <div className="ap-section max-w-4xl text-center">
          <p className="ap-eyebrow mb-4">Ready?</p>
          <h2 className="ap-display ap-h2 text-[var(--ap-ink)] mb-6">
            India's EV revolution is hiring.<br />
            Your resume just isn't <span className="ap-grad-text">reaching them.</span>
          </h2>
          <p className="text-xl text-[var(--ap-ink-2)] mb-8">
            In 2 minutes, you'll know exactly what's blocking your applications — and exactly how to fix it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={startAnalysis}
              className="ap-btn ap-btn-fire ap-btn-lg"
            >
              Start My Free Analysis
              <ArrowRight className="w-5 h-5" />
            </button>
            <a href="#features" className="ap-btn ap-btn-ghost ap-btn-lg">
              See All Features
            </a>
          </div>
          <div className="mt-6">
            <a href="/find-jobs" className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink-2)] underline underline-offset-2">
              Or find a free job →
            </a>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <StickyCTABar heroRef={heroRef} onCTAClick={startAnalysis} />
    </div>
  );
}
